/* Public article identity and complete-result pagination; also exercised in Node. */
(function (root) {
  'use strict';
  const normalize = value => String(value || '').normalize('NFKC').toLocaleLowerCase('de').replace(/\s+/g, ' ').trim();
  const skuKey = value => String(value || '').trim().toUpperCase();
  const words = value => normalize(value).replace(/[^\p{L}\p{N}+]+/gu, ' ').trim();
  const containsPhrase = (text, phrase) => (` ${words(text)} `).includes(` ${words(phrase)} `);

  // Normalize measurements only, leaving model names and article numbers intact.
  // Keep decimal digits and rim suffixes: 2.5 must never become 25 or 2.
  const measurements = /(?<![\p{L}\p{N}_])\d+(?:[.,]\d+)?(?:\s*[x×*]\s*\d+(?:[.,]\d+)?){1,2}(?:\s*-\s*\d+(?:[.,]\d+)?)?(?![\p{N}.,])|(?<![\p{L}\p{N}_])\d+[.,]\d+(?!\p{N})/giu;
  const measurementKey = value => normalize(value).replace(/,/g, '.').replace(/\s*[x×*]\s*/g, 'x').replace(/\s*-\s*/g, '-');
  const normalizeSearch = query => normalize(query).replace(measurements, measurementKey);

  function storefrontQuery(query) {
    return normalizeSearch(query).replace(measurements, value => {
      const decimals = [value];
      // Include mixed decimal styles in dimensions such as 8,5x2.0.
      for (let index = 0; index < value.length; index++) {
        if (value[index] === '.') decimals.push(...decimals.map(text => text.slice(0, index) + ',' + text.slice(index + 1)));
      }
      const aliases = new Set(decimals.flatMap(text => ['x', ' x ', '×', ' × '].map(separator => text.replace(/x/g, separator))));
      // Each spelling stays one numeric phrase in the Shopify query.
      return '(' + [...aliases].map(text => '"' + text + '"').join(' OR ') + ')';
    });
  }

  function aiSearchPrompt(query, vehicle, filters = []) {
    let partQuery = String(query || '').trim();
    if (vehicle) partQuery = resolveVehicle(partQuery, [vehicle])?.query || partQuery;
    const request = partQuery ? `Ich suche: ${partQuery}.` : 'Ich suche ein Ersatzteil.';
    return [request, vehicle ? `Mein Scooter: ${vehicle.label}.` : '',
      filters.length ? `Meine Filter: ${filters.join(', ')}.` : '',
      'Kannst du mir bei der Suche helfen?'].filter(Boolean).join(' ');
  }

  function resolveVehicle(query, vehicles) {
    const normalizedQuery = normalize(query);
    // "10 x 2.5" contains the recorded model alias "X 2". Hide measurements
    // from model recognition, preserving positions for removal from the real query.
    const modelQuery = normalizedQuery.replace(measurements, value => /[x×*]/i.test(value) ? ' '.repeat(value.length) : value);
    const matches = vehicles.flatMap(vehicle => [vehicle.label, ...vehicle.aliases]
      .filter(alias => words(alias).length > 2 && containsPhrase(modelQuery, alias))
      .map(alias => ({ vehicle, alias, length: words(alias).length })));
    if (!matches.length) return null;
    const longest = Math.max(...matches.map(match => match.length));
    const best = matches.filter(match => match.length === longest);
    if (new Set(best.map(match => match.vehicle.id)).size !== 1) return null;
    const match = best[0];
    // Never broaden Pro/Plus/region/generation queries by accepting a shorter model.
    const literalAlias = normalize(match.alias);
    const index = modelQuery.indexOf(literalAlias);
    if (index < 0) return null;
    const remainder = (normalizedQuery.slice(0, index) + ' ' + normalizedQuery.slice(index + literalAlias.length)).replace(/\s+/g, ' ').trim();
    if (/\b(pro|plus|lite|max|ultra|gen\w*|generation|2nd|3rd|4th)\b|\+/.test(remainder)) return null;
    return { vehicle: match.vehicle, query: remainder.replace(/\b(für|fuer|for)\b/g, '').trim() };
  }

  function packSignature(title) {
    return (normalize(title).match(/\b\d+\s*(?:er[ -]?(?:set|pack)?|stück|stueck|pcs|pack|paar)\b|\b(?:set|kit|paar|gebraucht|refurbished|b-ware)\b/g) || []).sort().join('|');
  }

  function identity(product, variant, source) {
    const sku = skuKey(variant.sku);
    // Only Shopify-recorded SKUs can join separate Shopify listings. Options, units,
    // bundles, condition and barcode differences intentionally stay apart.
    // Listing prices and shipping weights do not change article identity.
    if (!source.skus[sku] || source.variantSkus[String(variant.id)] !== sku || product.requiresSellingPlan || variant.requiresComponents) return `variant:${variant.id}`;
    return JSON.stringify([sku, variant.barcode || '', variant.options,
      variant.unitMeasurement, normalize(source.variantConditions?.[String(variant.id)] ?? product.condition), product.optionNames, packSignature(product.title)]);
  }

  function modelTitleMatch(title, alias) {
    const text = ` ${words(title)} `, phrase = ` ${words(alias)} `;
    let index = text.indexOf(phrase);
    while (index >= 0) {
      const suffix = text.slice(index + phrase.length);
      // Do not credit a shorter model name inside a different region, trim or generation.
      if (!/^(?:pro|plus|lite|max|ultra|gen\w*|generation|ii|iii|iv|2nd|3rd|4th|d|e|\+)(?:\s|$)/.test(suffix)) return true;
      index = text.indexOf(phrase, index + 1);
    }
    return false;
  }

  function listingRank(item, vehicle) {
    const brands = Array.isArray(item.product.brands) ? item.product.brands : [];
    const generic = !brands.length || brands.some(brand => normalize(brand) === 'generic');
    const aliases = vehicle ? [vehicle.label, ...vehicle.aliases] : [];
    const labels = Array.isArray(item.product.models) ? item.product.models : [];
    const exactModel = aliases.some(alias => labels.some(label => words(label) === words(alias)));
    const titleMatch = aliases.some(alias => modelTitleMatch(item.product.title, alias));
    // This only ranks listings AFTER listing-specific Shopify fitment and identity grouping.
    // A regional F2 Pro D/E can prefer a family-titled F2 Pro listing; never drop
    // Pro/Plus/generation qualifiers or use this as new compatibility evidence.
    const familyAliases = aliases.map(words).filter(alias => /\s[de]$/.test(alias))
      .map(alias => alias.slice(0, -2)).filter(alias => alias.length > 2);
    const familyMatch = familyAliases.some(alias => modelTitleMatch(item.product.title, alias));
    return (exactModel ? 100 : 0) + (titleMatch ? 80 : familyMatch ? 70 : generic ? 60 : 0) + (item.variant.available ? 200 : 0);
  }

  function groupProducts(products, source, vehicleId = '', exactSku = '') {
    const groups = new Map();
    const vehicle = source.vehicles.find(value => value.id === vehicleId);
    if (vehicleId && !vehicle) throw new Error('Unknown vehicle');
    for (const product of products) {
      for (const variant of product.variants) {
        const sku = skuKey(variant.sku);
        if (exactSku && sku !== skuKey(exactSku)) continue;
        const models = source.productModels[String(product.id)] || [];
        if (vehicleId && !models.includes(vehicleId)) continue;
        const key = identity(product, variant, source);
        if (!groups.has(key)) groups.set(key, { key, sku, models, listings: [] });
        groups.get(key).listings.push({ product, variant });
      }
    }
    return [...groups.values()].map(group => {
      group.listings.sort((a, b) => listingRank(b, vehicle) - listingRank(a, vehicle) || a.product.id - b.product.id || a.variant.id - b.variant.id);
      const representative = group.listings[0];
      return { ...group, ...representative, models: source.productModels[String(representative.product.id)] || [] };
    });
  }

  function rankGroups(groups, query) {
    const terms = words(normalizeSearch(query)).split(' ').filter(Boolean);
    const score = group => terms.reduce((sum, term) => sum +
      (words(group.product.type) === term ? 100 : containsPhrase(group.product.type, term) ? 60 : 0) +
      (containsPhrase(normalizeSearch(group.product.title), term) ? 20 : 0), 0) + (group.variant.available ? 10 : 0);
    return [...groups].sort((a, b) => score(b) - score(a) || a.product.id - b.product.id);
  }

  function selectPage(groups, filters, pageSize = 24) {
    let selected = groups.filter(group => (!filters.type || group.product.type === filters.type) &&
      (!filters.types || filters.types.has(group.product.type)) &&
      (!filters.stock || group.variant.available) && (!filters.max || group.variant.price <= filters.max));
    if (filters.sort === 'price-asc') selected.sort((a, b) => a.variant.price - b.variant.price || a.product.id - b.product.id);
    if (filters.sort === 'price-desc') selected.sort((a, b) => b.variant.price - a.variant.price || a.product.id - b.product.id);
    if (filters.sort === 'title') selected.sort((a, b) => a.product.title.localeCompare(b.product.title, 'de'));
    const pages = Math.max(1, Math.ceil(selected.length / pageSize));
    const page = Math.min(Math.max(1, filters.page || 1), pages);
    return { items: selected.slice((page - 1) * pageSize, page * pageSize), count: selected.length, page, pages };
  }

  const api = { normalize, normalizeSearch, storefrontQuery, aiSearchPrompt, skuKey, words, resolveVehicle, packSignature, identity, groupProducts, rankGroups, selectPage };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.EsvGroupedSearch = api;
})(typeof window === 'undefined' ? globalThis : window);
